/**
 * Collectie-facetten: filter-, sorteer-, chip- en pagineringsacties verversen
 * de sectie via de Section Rendering API in plaats van een page reload.
 * Zonder JS werkt alles als gewoon GET-formulier / gewone links.
 */

const SECTION_SELECTOR = '[data-collection-section]';
const mobile = window.matchMedia('(max-width: 47.99rem)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

let fetchController = null;
let debounceTimer = null;

const currentRoot = () => document.querySelector(SECTION_SELECTOR);

// Op mobiel start het filterpaneel dichtgeklapt (in de markup staat het open,
// zodat het zonder JS altijd bereikbaar is)
function collapseFiltersOnMobile(root) {
  if (!mobile.matches) return;
  root.querySelector('[data-filters-wrap]')?.removeAttribute('open');
}

function buildSearchParams(form) {
  const params = new URLSearchParams();
  for (const [key, value] of new FormData(form)) {
    if (String(value).trim() !== '') params.append(key, value);
  }
  return params;
}

// Open/dicht-status van filtergroepen en het mobiele paneel overnemen
function preserveOpenState(oldRoot, newRoot) {
  for (const group of oldRoot.querySelectorAll('[data-facet-key]')) {
    const match = newRoot.querySelector(`[data-facet-key="${CSS.escape(group.dataset.facetKey)}"]`);
    match?.toggleAttribute('open', group.hasAttribute('open'));
  }
  const oldWrap = oldRoot.querySelector('[data-filters-wrap]');
  const newWrap = newRoot.querySelector('[data-filters-wrap]');
  if (oldWrap && newWrap) newWrap.toggleAttribute('open', oldWrap.hasAttribute('open'));
}

async function renderPage(searchParams, { pushHistory = true, scrollToTop = false } = {}) {
  const root = currentRoot();
  if (!root) return;

  root.querySelector('.collection__main')?.setAttribute('aria-busy', 'true');
  fetchController?.abort();
  fetchController = new AbortController();

  const fetchParams = new URLSearchParams(searchParams);
  fetchParams.set('section_id', root.dataset.sectionId);
  const pageUrl = searchParams.size
    ? `${window.location.pathname}?${searchParams}`
    : window.location.pathname;

  try {
    const res = await fetch(`${window.location.pathname}?${fetchParams}`, {
      signal: fetchController.signal,
    });
    if (!res.ok) throw new Error(`Section render: ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = doc.querySelector(SECTION_SELECTOR);
    if (!fresh) throw new Error('Sectie niet gevonden in respons');

    preserveOpenState(root, fresh);
    // Nieuwe reveal-elementen niet opnieuw laten animeren/verstoppen
    for (const el of fresh.querySelectorAll('[data-reveal]')) el.classList.add('is-revealed');

    // Focus-herstel: replaceWith gooit de toetsenbordpositie weg. Zoek na de
    // swap hetzelfde element terug (op name/value of id); lukt dat niet (bv.
    // een verdwenen chip), parkeer de focus op de productteller.
    const actief = document.activeElement;
    let focusSelector = null;
    if (root.contains(actief)) {
      if (actief.name) {
        focusSelector = `[name="${CSS.escape(actief.name)}"]`;
        if (actief.value && actief.type === 'checkbox') focusSelector += `[value="${CSS.escape(actief.value)}"]`;
      } else if (actief.id) {
        focusSelector = `#${CSS.escape(actief.id)}`;
      } else {
        focusSelector = '.collection__count';
      }
    }

    root.replaceWith(fresh);

    if (focusSelector) {
      const terug = fresh.querySelector(focusSelector) ?? fresh.querySelector('.collection__count');
      terug?.focus({ preventScroll: true });
    }

    if (pushHistory) history.pushState({}, '', pageUrl);
    if (scrollToTop) {
      fresh.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }
  } catch (error) {
    if (error.name === 'AbortError') return;
    window.location.href = pageUrl; // val terug op gewone navigatie
  }
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (input.form?.id !== 'facets-form' || !currentRoot()) return;
  const delay = input.type === 'number' ? 600 : 150;
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => renderPage(buildSearchParams(input.form)), delay);
});

document.addEventListener('submit', (event) => {
  if (event.target.id !== 'facets-form' || !currentRoot()) return;
  event.preventDefault();
  clearTimeout(debounceTimer);
  renderPage(buildSearchParams(event.target));
});

document.addEventListener('click', (event) => {
  // Ctrl/Cmd/Shift-klik: niet kapen, de browser opent gewoon een nieuw tabblad
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
  const link = event.target.closest(
    '.collection__chip, .collection__chips-clear, .collection__pagination a, .collection__empty a'
  );
  if (!link || !link.closest(SECTION_SELECTOR)) return;
  const url = new URL(link.href, window.location.origin);
  if (url.pathname !== window.location.pathname) return; // andere pagina: gewoon navigeren
  event.preventDefault();
  renderPage(url.searchParams, { scrollToTop: link.closest('.collection__pagination') !== null });
});

window.addEventListener('popstate', () => {
  if (!currentRoot()) return;
  renderPage(new URLSearchParams(window.location.search), { pushHistory: false });
});

const root = currentRoot();
if (root) collapseFiltersOnMobile(root);
